import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/firebase';
import { collection, onSnapshot, addDoc, updateDoc, doc, deleteDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import {
  TableTemplate, ScheduledTable, ScheduledTableData,
  GameType, TableStatus, GAME_TYPES, TABLE_STATUSES,
  ScheduleFrequency, RecurrenceRule, EndCondition,
  SCHEDULE_FREQUENCIES, DAY_LABELS,
} from '@shared/types';
import { LoadingSpinner } from '../common/LoadingSpinner';

// ===== Recurrence helpers =====

/**
 * Calculate the very first `next_run_time_utc` for a schedule.
 * For 'once': just the selected date+time.
 * For recurring: the next valid occurrence at or after startDate + timeOfDay,
 * factoring in selected days_of_week for weekly.
 */
function calculateFirstRunTime(
  frequency: ScheduleFrequency,
  startDate: string,         // "YYYY-MM-DD"
  timeOfDay: string,         // "HH:mm"
  daysOfWeek?: number[],     // 0=Sun..6=Sat
): Date {
  if (frequency === 'once') {
    return new Date(`${startDate}T${timeOfDay}`);
  }

  const parts = timeOfDay.split(':');
  const hours = parseInt(parts[0] || '18', 10);
  const minutes = parseInt(parts[1] || '0', 10);

  if (frequency === 'daily') {
    const d = new Date(`${startDate}T${timeOfDay}`);
    // If the date/time is in the past, move to tomorrow
    if (d <= new Date()) {
      d.setDate(d.getDate() + 1);
    }
    return d;
  }

  // weekly — find earliest matching day on or after startDate
  if (!daysOfWeek || daysOfWeek.length === 0) {
    return new Date(`${startDate}T${timeOfDay}`);
  }

  const base = new Date(`${startDate}T00:00:00`);
  for (let offset = 0; offset < 7; offset++) {
    const candidate = new Date(base);
    candidate.setDate(candidate.getDate() + offset);
    candidate.setHours(hours, minutes, 0, 0);
    if (daysOfWeek.includes(candidate.getDay()) && candidate > new Date()) {
      return candidate;
    }
  }
  // Fallback — just use start date
  return new Date(`${startDate}T${timeOfDay}`);
}

// ===== Component =====

interface ScheduleManagerProps {
  roomId: string;
  templateToSchedule: TableTemplate | null;
  onClearTemplate: () => void;
}

export function ScheduleManager({ roomId, templateToSchedule, onClearTemplate }: ScheduleManagerProps) {
  const { userProfile } = useAuth();
  const [scheduledTables, setScheduledTables] = useState<ScheduledTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<ScheduledTable | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('scheduled');

  // Real-time listener
  useEffect(() => {
    if (!roomId || !db) {
      setLoading(false);
      return;
    }
    const scheduledRef = collection(db, 'rooms', roomId, 'scheduled_tables');
    const unsubscribe = onSnapshot(
      scheduledRef,
      (snapshot) => {
        const data = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data(),
        })) as ScheduledTable[];
        setScheduledTables(data);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [roomId]);

  // --- Create ---
  const handleScheduleTable = async (
    tableData: ScheduledTableData,
    frequency: ScheduleFrequency,
    startDate: string,
    timeOfDay: string,
    daysOfWeek: number[],
    endCondition: EndCondition,
    endDate: string,
    maxOccurrences: number,
    templateId: string,
  ) => {
    try {
      setActionLoading(true);
      setError(null);

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const firstRun = calculateFirstRunTime(frequency, startDate, timeOfDay, daysOfWeek);

      if (isNaN(firstRun.getTime())) {
        setError('Please select a valid date and time');
        return;
      }
      if (firstRun <= new Date()) {
        setError('Scheduled time must be in the future');
        return;
      }

      const docData: Record<string, unknown> = {
        room_id: roomId,
        template_id: templateId,
        table_data: tableData,
        type: frequency,
        next_run_time_utc: Timestamp.fromDate(firstRun),
        timezone,
        occurrences_count: 0,
        status: 'scheduled',
        created_by: userProfile?.uid || '',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      if (frequency !== 'once') {
        const rule: RecurrenceRule = {
          frequency: frequency as 'daily' | 'weekly',
          time_of_day: timeOfDay,
          start_date: startDate,
          end_condition: endCondition,
        };
        if (frequency === 'weekly') {
          rule.days_of_week = daysOfWeek;
        }
        if (endCondition === 'date') {
          rule.end_date = endDate;
        }
        if (endCondition === 'occurrences') {
          rule.max_occurrences = maxOccurrences;
        }
        docData.recurrence_rule = rule;
      }

      const scheduledRef = collection(db, 'rooms', roomId, 'scheduled_tables');
      await addDoc(scheduledRef, docData);
      onClearTemplate();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to schedule table');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Update ---
  const handleUpdateSchedule = async (
    scheduleId: string,
    tableData: ScheduledTableData,
    frequency: ScheduleFrequency,
    startDate: string,
    timeOfDay: string,
    daysOfWeek: number[],
    endCondition: EndCondition,
    endDate: string,
    maxOccurrences: number,
  ) => {
    try {
      setActionLoading(true);
      setError(null);

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const firstRun = calculateFirstRunTime(frequency, startDate, timeOfDay, daysOfWeek);

      if (isNaN(firstRun.getTime())) {
        setError('Please select a valid date and time');
        return;
      }
      if (firstRun <= new Date()) {
        setError('Scheduled time must be in the future');
        return;
      }

      const updateData: Record<string, unknown> = {
        table_data: tableData,
        type: frequency,
        next_run_time_utc: Timestamp.fromDate(firstRun),
        timezone,
        updated_at: serverTimestamp(),
      };

      if (frequency !== 'once') {
        const rule: RecurrenceRule = {
          frequency: frequency as 'daily' | 'weekly',
          time_of_day: timeOfDay,
          start_date: startDate,
          end_condition: endCondition,
        };
        if (frequency === 'weekly') rule.days_of_week = daysOfWeek;
        if (endCondition === 'date') rule.end_date = endDate;
        if (endCondition === 'occurrences') rule.max_occurrences = maxOccurrences;
        updateData.recurrence_rule = rule;
      } else {
        updateData.recurrence_rule = null; // Clear rule if switching to once
      }

      const scheduleRef = doc(db, 'rooms', roomId, 'scheduled_tables', scheduleId);
      await updateDoc(scheduleRef, updateData);
      setEditingSchedule(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update schedule');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Cancel ---
  const handleCancelSchedule = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to cancel this scheduled table?')) return;
    try {
      setActionLoading(true);
      setError(null);
      const scheduleRef = doc(db, 'rooms', roomId, 'scheduled_tables', scheduleId);
      await updateDoc(scheduleRef, {
        status: 'cancelled',
        updated_at: serverTimestamp(),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel schedule');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Delete ---
  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!confirm('Are you sure you want to permanently delete this schedule? This cannot be undone.')) return;
    try {
      setActionLoading(true);
      setError(null);
      const scheduleRef = doc(db, 'rooms', roomId, 'scheduled_tables', scheduleId);
      await deleteDoc(scheduleRef);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Clear All ---
  const handleClearAll = async () => {
    const listToClear = filteredSchedules;
    if (listToClear.length === 0) return;
    
    if (!confirm(`Are you sure you want to permanently delete ${listToClear.length} table(s) from the "${statusFilter.toUpperCase()}" list? This cannot be undone.`)) return;

    try {
      setActionLoading(true);
      setError(null);
      await Promise.all(listToClear.map(schedule => 
        deleteDoc(doc(db, 'rooms', roomId, 'scheduled_tables', schedule.id))
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear schedules');
    } finally {
      setActionLoading(false);
    }
  };

  // --- Helpers ---
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'executed': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatNextRun = (schedule: ScheduledTable) => {
    try {
      const date = schedule.next_run_time_utc.toDate();
      return date.toLocaleString('en-US', {
        timeZone: schedule.timezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getRecurrenceLabel = (schedule: ScheduledTable) => {
    if (schedule.type === 'once') return 'One-time';
    if (schedule.type === 'daily') return 'Daily';
    if (schedule.type === 'weekly' && schedule.recurrence_rule?.days_of_week) {
      const dayNames = schedule.recurrence_rule.days_of_week.map(d => DAY_LABELS[d]).join(', ');
      return `Weekly (${dayNames})`;
    }
    return schedule.type;
  };

  const getEndLabel = (schedule: ScheduledTable) => {
    if (schedule.type === 'once') return '';
    const rule = schedule.recurrence_rule;
    if (!rule) return '';
    if (rule.end_condition === 'never') return 'Never ends';
    if (rule.end_condition === 'date') return `Ends ${rule.end_date}`;
    if (rule.end_condition === 'occurrences') return `${schedule.occurrences_count}/${rule.max_occurrences} runs`;
    return '';
  };

  const filteredSchedules = scheduledTables
    .filter(s => statusFilter === 'all' || s.status === statusFilter)
    .sort((a, b) => {
      if (a.status === 'scheduled' && b.status === 'scheduled') {
        const aTime = a.next_run_time_utc?.toDate?.()?.getTime?.() || 0;
        const bTime = b.next_run_time_utc?.toDate?.()?.getTime?.() || 0;
        return aTime - bTime;
      }
      if (a.status === 'scheduled') return -1;
      if (b.status === 'scheduled') return 1;
      return 0;
    });

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Scheduled Tables</h2>
          <p className="text-sm text-gray-500 mt-1">Tables that will be automatically created at the scheduled time</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Filter:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="input w-auto text-sm"
            >
              <option value="all">All</option>
              <option value="scheduled">Scheduled</option>
              <option value="executed">Executed</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          
          <button
            onClick={handleClearAll}
            disabled={actionLoading || filteredSchedules.length === 0}
            className="btn btn-outline text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 text-sm whitespace-nowrap"
          >
            Clear All
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Schedule Form — shown when a template is selected for scheduling */}
      {templateToSchedule && (
        <ScheduleForm
          initialData={{
            name: templateToSchedule.name,
            game_type: templateToSchedule.game_type,
            custom_game_type: templateToSchedule.custom_game_type,
            stakes: templateToSchedule.stakes,
            number_of_tables: templateToSchedule.number_of_tables,
            status: templateToSchedule.status,
            waitlist_limit: templateToSchedule.waitlist_limit,
          }}
          templateId={templateToSchedule.id}
          onSubmit={(tableData, freq, startDate, timeOfDay, days, endCond, endDt, maxOcc) =>
            handleScheduleTable(tableData, freq, startDate, timeOfDay, days, endCond, endDt, maxOcc, templateToSchedule.id)
          }
          onCancel={onClearTemplate}
          loading={actionLoading}
          title="Schedule Table"
          submitText="Schedule Table"
        />
      )}

      {/* Edit Schedule Form */}
      {editingSchedule && (
        <ScheduleForm
          initialData={editingSchedule.table_data}
          templateId={editingSchedule.template_id}
          initialSchedule={editingSchedule}
          onSubmit={(tableData, freq, startDate, timeOfDay, days, endCond, endDt, maxOcc) =>
            handleUpdateSchedule(editingSchedule.id, tableData, freq, startDate, timeOfDay, days, endCond, endDt, maxOcc)
          }
          onCancel={() => setEditingSchedule(null)}
          loading={actionLoading}
          title="Edit Scheduled Table"
          submitText="Update Schedule"
        />
      )}

      {/* Scheduled Tables List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      ) : filteredSchedules.length === 0 ? (
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-500">
            {statusFilter === 'all' ? 'No scheduled tables yet.' : `No ${statusFilter} tables.`}
          </p>
          <p className="text-sm text-gray-400 mt-1">Click "Schedule" on a template above to schedule a table.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Game / Stakes</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Run</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSchedules.map((schedule) => (
                <tr key={schedule.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{schedule.table_data.name}</div>
                    <div className="text-xs text-gray-400">{schedule.table_data.number_of_tables} table(s)</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {schedule.table_data.game_type} · {schedule.table_data.stakes}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    <div>{getRecurrenceLabel(schedule)}</div>
                    {schedule.type !== 'once' && (
                      <div className="text-xs text-gray-400 mt-0.5">{getEndLabel(schedule)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {schedule.status === 'scheduled' ? formatNextRun(schedule) : '—'}
                    <div className="text-xs text-gray-400">{schedule.timezone}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide ${getStatusBadge(schedule.status)}`}>
                      {schedule.status}
                    </span>
                    {schedule.occurrences_count > 0 && (
                      <div className="text-xs text-gray-400 mt-0.5">{schedule.occurrences_count} run(s)</div>
                    )}
                    {schedule.error_message && (
                      <p className="text-xs text-red-500 mt-1">{schedule.error_message}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      {schedule.status === 'scheduled' ? (
                        <>
                          <button
                            onClick={() => setEditingSchedule(schedule)}
                            className="btn btn-outline btn-sm"
                            disabled={actionLoading}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleCancelSchedule(schedule.id)}
                            className="btn btn-danger btn-sm"
                            disabled={actionLoading}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          className="btn btn-outline text-red-600 border-red-200 hover:bg-red-50 text-xs py-1 px-2"
                          disabled={actionLoading}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===== Schedule Form Modal =====

interface ScheduleFormProps {
  initialData: ScheduledTableData;
  templateId: string;
  initialSchedule?: ScheduledTable;
  onSubmit: (
    tableData: ScheduledTableData,
    frequency: ScheduleFrequency,
    startDate: string,
    timeOfDay: string,
    daysOfWeek: number[],
    endCondition: EndCondition,
    endDate: string,
    maxOccurrences: number,
  ) => void;
  onCancel: () => void;
  loading: boolean;
  title: string;
  submitText: string;
}

function ScheduleForm({
  initialData, initialSchedule, onSubmit, onCancel, loading, title, submitText,
}: ScheduleFormProps) {
  // Table data
  const [formData, setFormData] = useState<ScheduledTableData>({
    name: initialData.name,
    game_type: initialData.game_type,
    custom_game_type: initialData.custom_game_type,
    stakes: initialData.stakes,
    number_of_tables: initialData.number_of_tables,
    status: initialData.status,
    waitlist_limit: initialData.waitlist_limit,
  });

  // Scheduling data
  const existingRule = initialSchedule?.recurrence_rule;
  const [frequency, setFrequency] = useState<ScheduleFrequency>(initialSchedule?.type || 'once');

  // Separate date and time
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(() => {
    if (existingRule?.start_date) return existingRule.start_date;
    if (initialSchedule?.next_run_time_utc) {
      try { return initialSchedule.next_run_time_utc.toDate().toISOString().slice(0, 10); } catch { /* noop */ }
    }
    return today;
  });

  const [timeOfDay, setTimeOfDay] = useState(() => {
    if (existingRule?.time_of_day) return existingRule.time_of_day;
    if (initialSchedule?.next_run_time_utc) {
      try {
        const d = initialSchedule.next_run_time_utc.toDate();
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      } catch { /* noop */ }
    }
    return '18:00';
  });

  // Weekly days
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(() => {
    if (existingRule?.days_of_week) return existingRule.days_of_week;
    return [];
  });

  // End condition
  const [endCondition, setEndCondition] = useState<EndCondition>(existingRule?.end_condition || 'never');
  const [endDate, setEndDate] = useState(existingRule?.end_date || '');
  const [maxOccurrences, setMaxOccurrences] = useState(existingRule?.max_occurrences || 10);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const toggleDay = (day: number) => {
    setDaysOfWeek(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
    );
  };

  const handleStatusChange = (newStatus: TableStatus) => {
    const updated = { ...formData, status: newStatus };
    if (newStatus === 'Interest' || newStatus === 'Closed') {
      updated.number_of_tables = 0;
    } else if ((newStatus === 'Open' || newStatus === 'Running') && updated.number_of_tables === 0) {
      updated.number_of_tables = 1;
    }
    setFormData(updated);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (frequency === 'weekly' && daysOfWeek.length === 0) {
      alert('Please select at least one day of the week');
      return;
    }

    const submitData: ScheduledTableData = { ...formData };
    if (formData.game_type !== 'Custom') {
      delete submitData.custom_game_type;
    }
    if (formData.waitlist_limit === undefined || formData.waitlist_limit === null) {
      delete submitData.waitlist_limit;
    }
    onSubmit(submitData, frequency, startDate, timeOfDay, daysOfWeek, endCondition, endDate, maxOccurrences);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">

          {/* ========== SCHEDULING SECTION ========== */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <div className="flex items-center text-blue-900 font-medium text-sm">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Schedule Settings
            </div>

            {/* Frequency Selector */}
            <div>
              <label className="label text-blue-900 text-xs uppercase tracking-wider">Frequency</label>
              <div className="flex rounded-lg overflow-hidden border border-blue-300">
                {SCHEDULE_FREQUENCIES.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => setFrequency(f.value)}
                    className={`flex-1 py-2 text-sm font-medium transition-colors ${
                      frequency === f.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Weekly Day Picker */}
            {frequency === 'weekly' && (
              <div>
                <label className="label text-blue-900 text-xs uppercase tracking-wider">Days of Week</label>
                <div className="flex gap-1.5">
                  {DAY_LABELS.map((label, i) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => toggleDay(i)}
                      className={`w-10 h-10 rounded-lg text-xs font-semibold transition-all ${
                        daysOfWeek.includes(i)
                          ? 'bg-blue-600 text-white shadow-md'
                          : 'bg-white border border-blue-200 text-blue-700 hover:bg-blue-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Date & Time row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label text-blue-900 text-xs uppercase tracking-wider">
                  {frequency === 'once' ? 'Date' : 'Start Date'}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={today}
                  className="input"
                  required
                />
              </div>
              <div>
                <label className="label text-blue-900 text-xs uppercase tracking-wider">Time</label>
                <input
                  type="time"
                  value={timeOfDay}
                  onChange={(e) => setTimeOfDay(e.target.value)}
                  className="input"
                  required
                />
              </div>
            </div>

            <p className="text-xs text-blue-600">Timezone: {timezone}</p>

            {/* End Condition — only for recurring */}
            {frequency !== 'once' && (
              <div className="pt-2 border-t border-blue-200">
                <label className="label text-blue-900 text-xs uppercase tracking-wider">Ends</label>
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio" name="end" value="never"
                      checked={endCondition === 'never'}
                      onChange={() => setEndCondition('never')}
                      className="mr-2 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Never</span>
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio" name="end" value="date"
                      checked={endCondition === 'date'}
                      onChange={() => setEndCondition('date')}
                      className="mr-2 text-blue-600"
                    />
                    <span className="text-sm text-gray-700 mr-2">On date</span>
                    {endCondition === 'date' && (
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="input w-auto text-sm"
                        required
                      />
                    )}
                  </label>

                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio" name="end" value="occurrences"
                      checked={endCondition === 'occurrences'}
                      onChange={() => setEndCondition('occurrences')}
                      className="mr-2 text-blue-600"
                    />
                    <span className="text-sm text-gray-700 mr-2">After</span>
                    {endCondition === 'occurrences' && (
                      <>
                        <input
                          type="number" min="1" max="365"
                          value={maxOccurrences}
                          onChange={(e) => setMaxOccurrences(parseInt(e.target.value) || 1)}
                          className="input w-20 text-sm"
                          required
                        />
                        <span className="text-sm text-gray-700 ml-2">occurrences</span>
                      </>
                    )}
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* ========== TABLE DETAILS SECTION ========== */}
          <div className="pt-2">
            <p className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3">Table Details</p>
          </div>

          <div>
            <label className="label">Table Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              placeholder="e.g., Friday $1/$3 NLH"
              required
            />
            <p className="text-xs text-gray-400 mt-1">You can customize without changing the original template</p>
          </div>

          <div>
            <label className="label">Game Type</label>
            <select
              value={formData.game_type}
              onChange={(e) => setFormData({ ...formData, game_type: e.target.value as GameType })}
              className="input"
            >
              {GAME_TYPES.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {formData.game_type === 'Custom' && (
            <div>
              <label className="label">Custom Game Type</label>
              <input
                type="text"
                value={formData.custom_game_type || ''}
                onChange={(e) => setFormData({ ...formData, custom_game_type: e.target.value })}
                className="input"
                required
              />
            </div>
          )}

          <div>
            <label className="label">Stakes</label>
            <input
              type="text"
              value={formData.stakes}
              onChange={(e) => setFormData({ ...formData, stakes: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">Status When Created</label>
            <select
              value={formData.status}
              onChange={(e) => handleStatusChange(e.target.value as TableStatus)}
              className="input"
            >
              {TABLE_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {(formData.status === 'Open' || formData.status === 'Running') && (
            <div>
              <label className="label">Number of Tables</label>
              <input
                type="number"
                min="1"
                max="50"
                value={formData.number_of_tables}
                onChange={(e) => setFormData({ ...formData, number_of_tables: parseInt(e.target.value) || 1 })}
                className="input"
                required
              />
            </div>
          )}

          {(formData.status === 'Interest' || formData.status === 'Closed') && (
            <div>
              <label className="label">Number of Tables</label>
              <input type="number" value="0" disabled className="input bg-gray-100" />
              <p className="text-sm text-gray-500 mt-1">
                Automatically set to 0 for {formData.status.toLowerCase()} status
              </p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-3 pt-4">
            <button type="button" onClick={onCancel} className="btn btn-outline" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <LoadingSpinner size="sm" className="mr-2" /> : (
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              {submitText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
